import { Injectable } from '@angular/core';
import {
  CartItem,
  EndOrderDetails,
  CouponConfigurationResponse,
  EstablishmentCheckout,
  isPaymentMethodWithLink,
} from '@pedix-workspace/utils';
import {
  ShippingCostResponse,
  hasOutOfDeliveryZone,
  hasShippingEstimate,
} from '@pedix-workspace/shared-delivery';
import { EndOrderForm } from './end-order-form/end-order-form.service';
import { EndOrderTotals } from './end-order-totals.service';
import { customFieldValuesToCustomFieldDetails } from '@pedix-workspace/orders';

export type NewEndOrderDetailsParams = {
  cartItems: CartItem[];
  establishmentId: string;
  couponConfigurationSnapshot?: CouponConfigurationResponse;
  shippingCostResponse?: ShippingCostResponse;
  endOrderForm: EndOrderForm;
  endOrderTotals: EndOrderTotals;
  checkoutData: EstablishmentCheckout;
  source: EndOrderDetails['source'];
  userUid?: string;
  copiedFrom?: string;
};
export type SubsidiarySelectedEvent = {
  selectedSubsidiaryId: string;
  establishmentCheckout?: EstablishmentCheckout;
};

@Injectable({
  providedIn: 'root',
})
export class EndOrderService {
  createEndOrderDetails({
    cartItems,
    establishmentId,
    couponConfigurationSnapshot,
    shippingCostResponse,
    endOrderTotals,
    endOrderForm,
    checkoutData,
    source,
    userUid,
    copiedFrom,
  }: NewEndOrderDetailsParams): EndOrderDetails {
    const outOfDeliveryZone = hasOutOfDeliveryZone(shippingCostResponse);
    const shippingEstimate = hasShippingEstimate(shippingCostResponse)
      ? shippingCostResponse.shippingEstimate
      : undefined;

    return {
      created: new Date(),
      updated: new Date(),
      status: 'new',
      sequenceId: 0,
      establishmentId,
      cartItems,
      digitalPayment:
        endOrderForm.paymentMethod && isPaymentMethodWithLink(endOrderForm.paymentMethod)
          ? {
              type: 'link',
              status: 'PENDING',
              updated: new Date(),
              ...(endOrderTotals.installmentCount && {
                installmentCount: endOrderTotals.installmentCount,
                installmentSurchargePercent: endOrderTotals.installmentSurchargePercent,
              }),
            }
          : null,
      tags: [],
      schemaVersion: 7,
      source,
      copiedFrom,
      personalInformation: {
        name: endOrderForm.name,
        phone: endOrderForm.phone,
        email: endOrderForm.email,
        address: endOrderForm.address,
      },
      billingInformation: endOrderForm.requestBilling ? endOrderForm.billingInformation : null,
      totalAmount: endOrderTotals.totalAmount,
      totalAmountWithDiscount: endOrderTotals.totalAmountWithDiscount,
      totalProductDiscount: endOrderTotals.totalProductDiscount,
      promotionDiscount: endOrderTotals.promotionDiscount,
      deliveryCost: endOrderTotals.deliveryCost,
      couponDiscount: endOrderTotals.couponDiscount,
      paymentMethodExtraCharge: endOrderTotals.paymentMethodExtraCharge,
      paymentMethodDiscount: endOrderTotals.paymentMethodDiscount,
      installmentSurcharge: endOrderTotals.installmentSurcharge,
      finalAmount: endOrderTotals.finalAmount,
      couponConfigurationSnapshot: couponConfigurationSnapshot,
      customFieldDetails: customFieldValuesToCustomFieldDetails(
        checkoutData.customFields,
        endOrderForm.customFieldDetailValues || [],
      ),
      outOfDeliveryZone,
      paymentMethod: endOrderForm.paymentMethod,
      shippingOption: endOrderForm.shippingOption,
      tableNumber: endOrderForm.tableNumber,
      howMuch: endOrderForm.howMuch,
      shippingEstimate,
      userUid,
      currencyCode: endOrderTotals.currencyCode,
      // Food Bank Donation
      foodBankDonationAmount: endOrderTotals.foodBankDonationAmount,
    };
  }
}
